/**
 * CommunicationsHubPage — Batch #14.55
 *
 * Centre de contrôle admin : suivi de lecture des communications portail
 * (factures, documents, mandats). Permet de voir d'un coup d'œil ce qui a
 * été lu / non lu par les clients.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Inbox, Filter, RefreshCw, FileText, Receipt, FileSignature, BellRing, CheckCheck, BellOff, Bell, MessageSquare, Archive, ArchiveRestore } from "lucide-react";
import ReadStatusBadge from "@/components/shared/ReadStatusBadge";
import ReadToggleButton from "@/components/shared/ReadToggleButton";
import { useReadOverrides } from "@/hooks/useReadOverrides";

// Mapping vers les entity_type du service reminders
const KIND_TO_REMINDER_TYPE = {
  invoice: "invoice",
  document: "client_document",
  mandat: "mandat",
};

const KIND_META = {
  invoice:  { icon: Receipt,        color: "text-emerald-700 bg-emerald-50", label: "Facture" },
  document: { icon: FileText,       color: "text-blue-700 bg-blue-50",       label: "Document" },
  mandat:   { icon: FileSignature,  color: "text-violet-700 bg-violet-50",   label: "Mandat" },
  chat:     { icon: MessageSquare,  color: "text-indigo-700 bg-indigo-50",   label: "Chat" },
};

export default function CommunicationsHubPage() {
  const [data, setData] = useState({ rows: [], totals: { total: 0, read: 0, unread: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | read | unread | archived
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(new Set()); // Batch #14.234 — clés `${kind}:${id}`
  const { isOverridden, markRead, markUnread, markAllRead, resetAll, count: overrideCount } = useReadOverrides("communication");

  const isArchivedView = filter === "archived";

  // Filtre les lignes selon l'état "marqué admin" + filtre serveur
  const visibleRows = useMemo(() => {
    if (filter === "all" || isArchivedView) return data.rows;
    return data.rows.filter((r) => {
      const reallyUnread = !r.is_read;
      const effectiveUnread = reallyUnread && !isOverridden(r.id);
      return filter === "unread" ? effectiveUnread : !effectiveUnread;
    });
  }, [data.rows, filter, isArchivedView, isOverridden]);

  // Compteurs effectifs (côté admin)
  const effectiveTotals = useMemo(() => {
    const total = data.rows.length;
    let unread = 0;
    for (const r of data.rows) {
      if (!r.is_read && !isOverridden(r.id)) unread++;
    }
    return { total, read: total - unread, unread };
  }, [data.rows, isOverridden]);

  const markAllVisibleRead = async () => {
    const ids = data.rows
      .filter((r) => !r.is_read && !isOverridden(r.id))
      .map((r) => r.id);
    if (ids.length === 0) {
      toast.message("Rien à marquer", { description: "Toutes les communications sont déjà lues." });
      return;
    }
    if (!window.confirm(`Marquer comme lu ${ids.length} communication(s) ? (Vous pourrez les remettre en non-lu individuellement)`)) return;
    await markAllRead(ids);
  };

  const toggleReminders = async (row) => {
    const reminderType = KIND_TO_REMINDER_TYPE[row.kind];
    if (!reminderType) {
      toast.error("Type de relance non supporté pour ce document");
      return;
    }
    const currentlyDisabled = !!row.reminders_disabled;
    const action = currentlyDisabled ? "réactiver" : "désactiver";
    if (!window.confirm(`Voulez-vous ${action} les relances automatiques pour ce document ?`)) return;
    try {
      await api.post("/admin/reminders/toggle", {
        entity_type: reminderType,
        entity_id: row.id,
        disabled: !currentlyDisabled,
      });
      toast.success(currentlyDisabled ? "Relances réactivées" : "Relances désactivées pour ce client");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du toggle");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = filter === "archived"
        ? { status: "all", days, archived: true }
        : { status: "all", days };
      const r = await api.get("/admin/communications-hub", { params });
      setData(r.data || { rows: [], totals: { total: 0, read: 0, unread: 0 } });
    } catch (e) {
      toast.error("Impossible de charger le suivi des communications");
    } finally {
      setLoading(false);
    }
  }, [filter, days]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.234 — Sélection par cases à cocher pour archiver / restaurer
  const rowKey = (r) => `${r.kind}:${r.id}`;
  const toggleSelect = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(rowKey(r)));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(visibleRows.map(rowKey)));
  };
  const archiveSelection = async () => {
    const items = [...selected].map((k) => {
      const idx = k.indexOf(":");
      return { kind: k.slice(0, idx), id: k.slice(idx + 1) };
    });
    if (items.length === 0) return;
    const archiving = !isArchivedView;
    try {
      await api.post("/admin/communications-hub/archive", { items, archive: archiving });
      toast.success(archiving
        ? `${items.length} communication(s) archivée(s)`
        : `${items.length} communication(s) restaurée(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'archivage");
    }
  };

  // Batch #14.56 — Bouton "Relancer maintenant" pour déclencher le job manuellement
  const [relancing, setRelancing] = useState(false);
  const runRelance = async () => {
    if (!window.confirm("Envoyer une relance email à tous les clients dont les factures portail sont non-lues depuis le délai configuré ? Cette action respecte les paramètres anti-spam.")) {
      return;
    }
    setRelancing(true);
    try {
      const r = await api.post("/admin/communications-hub/run-relance");
      if (!r.data.enabled) {
        toast.error("Relance désactivée. Activez-la dans Paramètres → Facturation.");
      } else {
        const { scanned, sent, errors } = r.data;
        toast.success(`Relance : ${sent} envoyé(s), ${scanned - sent - errors} ignoré(s), ${errors} erreur(s)`);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la relance");
    } finally {
      setRelancing(false);
    }
  };

  const readPct = effectiveTotals.total > 0
    ? Math.round((effectiveTotals.read / effectiveTotals.total) * 100)
    : 0;

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="communications-hub-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-emerald-600" />
            Suivi comms
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Suivi en temps réel : factures, documents et mandats envoyés au portail client.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={markAllVisibleRead}
            disabled={effectiveTotals.unread === 0}
            className="text-xs sm:text-sm px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5 disabled:opacity-40"
            title="Marque toutes les communications non-lues comme lues (côté admin uniquement)"
            data-testid="comms-hub-mark-all-read"
          >
            <CheckCheck className="w-4 h-4" />
            Tout marquer lu ({effectiveTotals.unread})
          </button>
          {overrideCount > 0 && (
            <button
              onClick={() => {
                if (window.confirm(`Annuler les ${overrideCount} marquage(s) manuel(s) ? Les badges non-lus réapparaîtront.`)) {
                  resetAll();
                }
              }}
              className="text-xs sm:text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"
              data-testid="comms-hub-reset-overrides"
            >
              Annuler marquages ({overrideCount})
            </button>
          )}
          <button
            onClick={runRelance}
            disabled={relancing || effectiveTotals.unread === 0}
            className="text-xs sm:text-sm px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1.5 disabled:opacity-40"
            title={effectiveTotals.unread === 0 ? "Aucune communication non-lue à relancer" : "Envoyer une relance aux clients"}
            data-testid="comms-hub-relance"
          >
            <BellRing className={`w-4 h-4 ${relancing ? "animate-pulse" : ""}`} />
            Relancer non-lus
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs sm:text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="comms-hub-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-slate-500 mb-1">Total envoyé</div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800">{effectiveTotals.total}</div>
          <div className="text-[10px] sm:text-xs text-slate-400 mt-1">{days} derniers jours</div>
        </div>
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-emerald-700 mb-1 flex items-center gap-1">
            Lus
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-emerald-700">{effectiveTotals.read}</div>
          <div className="text-[10px] sm:text-xs text-emerald-600 mt-1">{readPct}% du total</div>
        </div>
        <div className="bg-white border-2 border-rose-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-rose-700 mb-1 flex items-center gap-1">
            Non lus
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-rose-700">{effectiveTotals.unread}</div>
          <div className="text-[10px] sm:text-xs text-rose-600 mt-1">à relancer si critique</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <div className="flex gap-1 flex-wrap">
          {[
            { v: "all", label: "Tous" },
            { v: "read", label: "Lus" },
            { v: "unread", label: "Non lus" },
            { v: "archived", label: "Archivés" },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => setFilter(b.v)}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                filter === b.v
                  ? b.v === "archived"
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`comms-hub-filter-${b.v}`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-slate-500">Fenêtre :</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded-md px-2 py-1"
            data-testid="comms-hub-days-select"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
        </div>
      </div>

      {/* Rows */}
      <div className="bg-white border rounded-xl divide-y" data-testid="comms-hub-list">
        {visibleRows.length > 0 && (
          <div className="p-3 sm:p-4 flex items-center gap-3 flex-wrap bg-slate-50/60">
            <label className="text-xs text-slate-600 inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-emerald-600"
                data-testid="comms-hub-select-all"
              />
              Tout sélectionner ({visibleRows.length})
            </label>
            {selected.size > 0 && (
              <button
                onClick={archiveSelection}
                className={`text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5 ${
                  isArchivedView ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"
                }`}
                data-testid="comms-hub-archive-selected"
              >
                {isArchivedView ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                {isArchivedView ? `Restaurer (${selected.size})` : `Archiver (${selected.size})`}
              </button>
            )}
          </div>
        )}
        {loading && data.rows.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
        )}
        {!loading && data.rows.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">
            {isArchivedView ? "Aucune communication archivée." : "Aucune communication trouvée pour ce filtre."}
          </div>
        )}
        {visibleRows.map((row) => {
          const Meta = KIND_META[row.kind] || KIND_META.document;
          const Icon = Meta.icon;
          const reallyUnread = !row.is_read;
          const overridden = isOverridden(row.id);
          return (
            <div
              key={`${row.kind}-${row.id}`}
              className="p-3 sm:p-4 flex items-center gap-3 hover:bg-slate-50/50"
              data-testid={`comms-hub-row-${row.id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(rowKey(row))}
                onChange={() => toggleSelect(rowKey(row))}
                className="w-4 h-4 accent-emerald-600 flex-shrink-0"
                data-testid={`comms-hub-select-${row.id}`}
              />
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg ${Meta.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                    {row.label}
                  </span>
                  <span className="text-[10px] sm:text-xs text-slate-400">·</span>
                  <span className="text-[10px] sm:text-xs text-slate-500 truncate">
                    {row.client_name}
                  </span>
                  {overridden && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Marqué lu
                    </span>
                  )}
                </div>
                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">
                  Envoyé {row.sent_at ? new Date(row.sent_at).toLocaleDateString("fr-CA") : "—"}
                </div>
              </div>
              <ReadStatusBadge readAt={row.read_at || (row.is_read ? row.sent_at : null)} readCount={row.read_count} size="sm" />
              <ReadToggleButton
                isReallyUnread={reallyUnread}
                isOverridden={overridden}
                onMarkRead={() => markRead(row.id)}
                onMarkUnread={() => markUnread(row.id)}
                testId={`comms-hub-toggle-${row.id}`}
              />
              {KIND_TO_REMINDER_TYPE[row.kind] && (
                <button
                  onClick={() => toggleReminders(row)}
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition ${
                    row.reminders_disabled
                      ? "text-slate-400 hover:bg-slate-100"
                      : "text-indigo-600 hover:bg-indigo-50"
                  }`}
                  title={row.reminders_disabled ? "Relances désactivées — cliquer pour réactiver" : "Désactiver les relances pour ce client"}
                  data-testid={`comms-hub-reminder-toggle-${row.id}`}
                >
                  {row.reminders_disabled ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Info légale */}
      <p className="text-[11px] text-slate-400 text-center">
        Le suivi enregistre uniquement l&apos;ouverture du document dans le portail client (pas par email).
        Les données sont conservées 1 an.
      </p>
    </div>
  );
}
